import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSidebar } from '../../context/SidebarContext';
import Icon from '../Common/Icon';
import UsefulLinksMenu from './UsefulLinksMenu';
import { canAccessOzelDosyalar } from '../../utils/ozelDosyaAccess';
import { caseFileAPI, epostaAPI, bakisAPI } from '../../api/api';
import { SON_BAKIS_ANAHTARI } from '../../pages/MuvekkilLoglari';
import './Sidebar.css';

const Sidebar = () => {
  const { isOpen, toggleSidebar, closeSidebar } = useSidebar();
  const [logsMenuOpen, setLogsMenuOpen] = useState(false);
  const [showUsefulLinks, setShowUsefulLinks] = useState(false);
  const [muvekkilYeniAdet, setMuvekkilYeniAdet] = useState(0);
  const [postaAdet, setPostaAdet] = useState(0);
  const location = useLocation();
  const { user } = useAuth();

  /* Müvekkil Logları rozeti: son bakıştan sonraki kayıt sayısı.
     Sayfaya girildiğinde "son bakış" güncellendiği için rozet orada sıfırlanır;
     burada yol değişimlerinde yeniden hesaplanır. Yalnızca yetkili kullanıcı
     için istek atılır, diğerleri boşuna sorgu yapmasın. */
  useEffect(() => {
    if (!canAccessOzelDosyalar(user)) { setMuvekkilYeniAdet(0); return undefined; }
    let iptal = false;

    const hesapla = async () => {
      try {
        // Son bakış sunucuda; cihaz değiştirince rozet sıfırlanmasın.
        let sonBakis = null;
        try { sonBakis = (await bakisAPI.getir())?.muvekkilLog ?? null; } catch (_) { /* yoksay */ }
        const d = await caseFileAPI.muvekkilTumLoglari(30, sonBakis);
        if (!iptal) setMuvekkilYeniAdet(Number(d?.yeniAdet) || 0);
      } catch (_) {
        if (!iptal) setMuvekkilYeniAdet(0);
      }
    };

    hesapla();
    // Uzun açık kalan oturumlarda da güncel kalsın.
    const zamanlayici = window.setInterval(hesapla, 5 * 60 * 1000);
    return () => { iptal = true; window.clearInterval(zamanlayici); };
  }, [user, location.pathname]);

  /* Gelen Postalar rozeti: bekleyen posta sayisi.
     Posta kutusu arka planda kendiliginden kontrol edildigi icin
     kullanici sayfaya girmeden de yeni posta gelebilir; rozet bunu
     menude gorunur kilar. Yalnizca sayiyi ceken hafif bir uc. */
  useEffect(() => {
    if (!canAccessOzelDosyalar(user)) { setPostaAdet(0); return undefined; }
    let iptal = false;

    const bak = async () => {
      try {
        // ROZET = SON BAKIŞTAN SONRA GELEN posta sayısı.
        // Eskiden "bekleyen toplam" gösteriliyordu; ilk okumadan kalan
        // 30 posta yüzünden rozet hiç kapanmıyordu. Bekleyen toplamı
        // zaten sayfanın kendi sekmesinde yazıyor.
        let sonBakis = null;
        try { sonBakis = (await bakisAPI.getir())?.gelenPosta ?? null; } catch (_) { /* yoksay */ }
        const d = await epostaAPI.bekleyenSayi(sonBakis);
        if (!iptal) setPostaAdet(Number(d?.yeniAdet ?? d?.YeniAdet ?? 0) || 0);
      } catch (_) {
        if (!iptal) setPostaAdet(0);
      }
    };

    bak();
    const zamanlayici = window.setInterval(bak, 3 * 60 * 1000);
    return () => { iptal = true; window.clearInterval(zamanlayici); };
  }, [user, location.pathname]);

  const isAdmin = () => {
    if (!user) return false;
    const role = user.Role ?? user.role ?? '';
    return role === 'Admin';
  };

  const canAccessKasa = () => {
    if (!user) return false;
    const userId = user.Id ?? user.id;
    return userId === 2 || userId === 8;
  };

  const isActive = (path) => {
    if (path === '/case-files') {
      return location.pathname === '/case-files' || location.pathname.startsWith('/case-files/');
    }
    if (path === '/ozel-dosyalar') {
      return location.pathname === '/ozel-dosyalar' || location.pathname.startsWith('/ozel-dosyalar/');
    }
    return location.pathname === path;
  };

  // Tekrar eden Link kalıbını tek yerde topladık; sıralamayı değiştirmek
  // artık aşağıdaki listeyi düzenlemek kadar kolay.
  // rozet: okunmamış kayıt sayısı (şimdilik yalnızca Müvekkil Logları kullanıyor)
  const item = (to, icon, label, rozet = 0) => (
    <Link
      key={to}
      to={to}
      className={`sidebar-item ${isActive(to) ? 'active' : ''}`}
      onClick={closeSidebar}
    >
      <Icon name={icon} size={18} className="sidebar-item-icon" /> {label}
      {rozet > 0 && <span className="sidebar-rozet">{rozet}</span>}
    </Link>
  );

  return (
    <>
      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h3>MENÜ</h3>
          <button className="sidebar-close" onClick={closeSidebar}>×</button>
        </div>
        <nav className="sidebar-nav">

          {/* ---------- Günlük kullanım: en üstte ---------- */}
          {item('/dashboard', 'home', 'Anasayfa')}
          {item('/case-files', 'folder', 'Dosyalar')}
          {canAccessOzelDosyalar(user) && item('/ozel-dosyalar', 'folder', 'Özel Dosyalar')}
          {item('/muvekkiller', 'users', 'Müvekkiller')}
          {canAccessOzelDosyalar(user) && item('/gelen-postalar', 'inbox', 'Gelen Postalar', postaAdet)}
          {canAccessOzelDosyalar(user) && item('/ayarlar', 'settings', 'Ayarlar')}

          {/* ---------- İş takibi ---------- */}
          <div className="sidebar-section-header">İŞ TAKİBİ</div>
          {item('/tasks', 'checkSquare', 'Görevler')}
          {item('/job-list', 'list', 'İş Listesi')}
          {isAdmin() && item('/job-requests', 'inbox', 'İş Talepleri')}
          {canAccessOzelDosyalar(user) && item('/logs/muvekkil', 'users', 'Müvekkil Logları', muvekkilYeniAdet)}

          {/* ---------- Hesaplama araçları ---------- */}
          <div className="sidebar-section-header">HESAPLAMA</div>
          {item('/tazminat-hesap', 'activity', 'Tazminat Hesabı')}
          {item('/yasal-faiz-hesap', 'activity', 'Yasal Faiz Hesapla')}
          {item('/vekalet-ucreti-hesapla', 'activity', 'Vekalet Ücreti Hesapla')}
          {item('/iscilik-alacaklari-hesap', 'activity', 'İşçilik Alacakları Hesapla')}
          {item('/arac-hasar-hesap', 'activity', 'Hasar ve Değer Kaybı')}
          {item('/makbuz-hesapla', 'activity', 'Makbuz Hesapla')}

          {/* ---------- Belgeler ---------- */}
          <div className="sidebar-section-header">BELGELER</div>
          {item('/matbu-dilekceler', 'document', 'Matbu Dilekçeler')}
          {item('/ibraname-muvafakatname', 'clipboard', 'İbraname - Muvafakatname')}
          {item('/makbuzlar', 'receipt', 'Makbuzlar')}

          {/* ---------- Mali ---------- */}
          <div className="sidebar-section-header">MALİ</div>
          {isAdmin() && item('/masraflar', 'wallet', 'Masraflar')}
          {isAdmin() && item('/masraf-talepleri', 'edit', 'Masraf Talepleri')}
          {canAccessKasa() && item('/kasa', 'vault', 'Kasa')}
          {canAccessOzelDosyalar(user) && item('/kisisel-giderler', 'wallet', 'Kişisel Giderler')}

          {/* ---------- Referans ---------- */}
          <div className="sidebar-section-header">REFERANS</div>
          {item('/referanslar', 'users', 'Referanslar')}
          {isAdmin() && item('/referans-yonetim', 'settings', 'Referans Yönetimi')}

          {/* ---------- Arşiv ---------- */}
          <div className="sidebar-section-header">ARŞİV</div>
          {item('/archived-files', 'archive', 'Arşiv')}
          {isAdmin() && item('/archive-requests', 'inbox', 'Arşiv İstekleri')}

          {/* ---------- Diğer ---------- */}
          <div className="sidebar-section-header">DİĞER</div>
          <button
            type="button"
            className="sidebar-item sidebar-item-button"
            onClick={() => {
              closeSidebar();
              setShowUsefulLinks(true);
            }}
          >
            <Icon name="link" size={18} className="sidebar-item-icon" /> Faydalı Linkler
          </button>

          {isAdmin() && (
            <div className="sidebar-submenu">
              <div
                className={`sidebar-item ${logsMenuOpen ? 'active' : ''}`}
                onClick={() => setLogsMenuOpen(!logsMenuOpen)}
                style={{ cursor: 'pointer' }}
              >
                <Icon name="activity" size={18} className="sidebar-item-icon" /> Loglar {logsMenuOpen ? '▼' : '▶'}
              </div>
              {logsMenuOpen && (
                <div className="sidebar-submenu-items">
                  <Link
                    to="/logs/user-login"
                    className={`sidebar-submenu-item ${isActive('/logs/user-login') ? 'active' : ''}`}
                    onClick={closeSidebar}
                  >
                    <Icon name="lock" size={16} className="sidebar-item-icon" /> Kullanıcı Giriş Logları
                  </Link>
                  <Link
                    to="/logs/case-file"
                    className={`sidebar-submenu-item ${isActive('/logs/case-file') ? 'active' : ''}`}
                    onClick={closeSidebar}
                  >
                    <Icon name="folder" size={16} className="sidebar-item-icon" /> Dosya Logları
                  </Link>
                  <Link
                    to="/logs/masraf"
                    className={`sidebar-submenu-item ${isActive('/logs/masraf') ? 'active' : ''}`}
                    onClick={closeSidebar}
                  >
                    <Icon name="wallet" size={16} className="sidebar-item-icon" /> Masraf Logları
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* ---------- Hesap ---------- */}
          <div className="sidebar-section-header">HESAP SAYFALARI</div>
          {isAdmin() && item('/users', 'users', 'Kullanıcılar')}
          {item('/profile', 'user', 'Profil')}
        </nav>
      </div>
      {isOpen && <div className="sidebar-overlay" onClick={closeSidebar}></div>}
      <UsefulLinksMenu
        isOpen={showUsefulLinks}
        onClose={() => setShowUsefulLinks(false)}
      />
    </>
  );
};

export default Sidebar;
